import { filter, fromValue, make, map, pipe } from 'wonka';
import { Tag } from '../tag';
import { addGlobalCallback } from '../io';
import { log } from '../logger';

const DV_TIMEOUT_MS = 5000;
export const DV_ALLOWED = 0;
export const DV_BLOCKED = 1;
export const DV_TIMED_OUT = 2;
export const DV_FAILED = 3;

type Callback = (result: number) => void;

/**
   * Given the URL for a DoubleVerify blocking tag, run that tag and then call the callback with
   * the result from them: DV_NOT_BLOCKED, DV_BLOCKED or DV_TIMEOUT
).
   * @param {String} url
   * @param {Node} container
   * @param {Function} callback
   */
export const handleDoubleVerifyBlockingTag = (
  url: string,
  container: Element,
  callback: Callback,
) => {
  // dbg(`Running DV blocking tag ${url}`);

  var timerId = 0;

  //  note - we specifically add the callback to our container's window so the
  //         iframe we add can find it easily
  const cwin = container.ownerDocument.defaultView || window;
  let dvContainer = document.createElement('iframe');
  const cb = addGlobalCallback<number>(cwin, (b) => {
    cwin.clearTimeout(timerId);
    timerId = 0;
    //  wrap callback in a timer so we distance ourselves from the iframe (like setimmediate)
    cwin.setTimeout(() => {
      callback(b);
    }, 1);
    //  need to remove the iframe now because the callback() above will continue
    //  our tag execution which will replace the contents of "container" - us.
    container.removeChild(dvContainer);
  });

  let html =
    '<head><sc' +
    'ript>function tellmbk(v){ window.frameElement.ownerDocument.defaultView.' +
    cb +
    '(v); }</script></head><body>' +
    '<sc' +
    'ript type="text/adtag"><img src="data:image/png,mobkoi" style="display: none" onerror="tellmbk(' +
    DV_ALLOWED +
    ')"></script>' +
    '<sc' +
    'ript type="text/passback"><img src="data:image/png,mobkoi" style="display: none" onerror="tellmbk(' +
    DV_BLOCKED +
    ')"></script>' +
    '<sc' +
    'ript src="' +
    url +
    '" onerror="tellmbk(' +
    DV_FAILED +
    ')"></script></body>';
  dvContainer.width = '0';
  dvContainer.height = '0';
  dvContainer.srcdoc = html;

  container.appendChild(dvContainer);

  //  a watchdog timer in case the dv tag fails and cb() is never called
  timerId = cwin.setTimeout(() => {
    timerId = 0;
    cwin[cb as keyof typeof cwin](DV_TIMED_OUT);
  }, DV_TIMEOUT_MS);
};

export const blockIfDoubleVerify$ = (tag: Tag) => {
  const { dvBlockUrl } = tag.pageOptions;

  if (!dvBlockUrl) {
    // No double verify, continue as normal
    return fromValue(tag);
  }

  // Load double verify check and check if our tag is blocked
  // if blocked, destroy the tag
  return pipe(
    make<number>((observer) => {
      let cancelled = false;
      handleDoubleVerifyBlockingTag(dvBlockUrl, tag.container!, (result) => {
        if (!cancelled) {
          observer.next(result);
          observer.complete();
        }
      });

      return () => {
        cancelled = true;
      };
    }),
    map((doubleVerifyResult) => {
      if (doubleVerifyResult === DV_BLOCKED) {
        log({
          reason: 'dv-blocked',
          tagId: tag.tagId,
          impressionId: tag.config.iid || '',
        });
        tag.destroy();
      }

      return tag;
    }),
    // DV will prevent tags from being run, no need to keep going if the tag
    // is destroyed, just filter it out
    filter((tag) => !tag.destroyed),
  );
};
